import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { useArtistCollabs } from "@web/features/library/api.ts";
import {
  openSharedTracksDrawer,
  useOpenDetail,
} from "@web/features/detail/useOpenDetail.ts";

/** Full collaborator list from local `track_artists` — never Spotify API. */
export function CollaboratorsSection({
  artistId,
  artistLabel,
}: {
  artistId: string;
  artistLabel: string;
}) {
  const collabs = useArtistCollabs(artistId);
  const openDetail = useOpenDetail();

  if (collabs.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (collabs.isError) {
    return (
      <EmptyState
        title="Couldn't load collaborators"
        description={collabs.error instanceof Error ? collabs.error.message : "Unknown error"}
      />
    );
  }

  const list = collabs.data ?? [];
  if (list.length === 0) {
    return (
      <EmptyState
        title="No collaborators in your library"
        description="Shared tracks are computed from artists credited together on tracks you already have."
      />
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-faint">
        {list.length.toLocaleString()} artists share at least one track with {artistLabel} in your
        library.
      </p>
      <ul className="divide-y divide-line rounded-md border border-line">
        {list.map((c) => (
          <li key={c.id} className="flex items-center gap-2 px-2 py-2">
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-3 text-left hover:bg-row-hover"
              onClick={() =>
                openDetail(
                  { type: "artist", id: c.id, label: c.name ?? "Artist" },
                  { mode: "push" },
                )
              }
            >
              <EntityAvatar src={c.image_url} alt="" size="sm" rounded="full" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium">
                {c.name ?? "Unknown"}
              </span>
            </button>
            <button
              type="button"
              className="shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-accent hover:bg-row-hover"
              onClick={() =>
                openSharedTracksDrawer({
                  artistId,
                  artistLabel,
                  withArtistId: c.id,
                  withLabel: c.name ?? "Artist",
                })
              }
            >
              {c.shared_tracks.toLocaleString()} shared
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
